/**
 * Number / currency / percent formatters scoped to the analytics surface.
 * Small and focused so each tab doesn't re-invent them.
 */

export function formatInt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return 'N/A';
  return Math.round(n).toLocaleString('en-US');
}

export function formatFloat(n: number | null | undefined, decimals = 2): string {
  if (n == null || Number.isNaN(n)) return 'N/A';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatPercent(
  n: number | null | undefined,
  decimals = 1,
  treatAsFraction = false,
): string {
  if (n == null || Number.isNaN(n)) return 'N/A';
  const val = treatAsFraction ? n * 100 : n;
  return `${val.toFixed(decimals)}%`;
}

export function formatCurrency(n: number | null | undefined, decimals = 2): string {
  if (n == null || Number.isNaN(n)) return 'N/A';
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/** 1234 → "1.2k"  |  1_234_567 → "1.2M" */
export function formatCompact(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return 'N/A';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

export function formatDelta(deltaPct: number | null | undefined): string {
  if (deltaPct == null) return 'N/A';
  const sign = deltaPct > 0 ? '+' : '';
  return `${sign}${deltaPct.toFixed(1)}%`;
}

/**
 * Classify a delta as positive/negative/flat for color selection.
 * "Flat" is |delta| < 0.5% to suppress noise.
 */
export function deltaDirection(deltaPct: number | null | undefined): 'up' | 'down' | 'flat' {
  if (deltaPct == null) return 'flat';
  if (deltaPct > 0.5) return 'up';
  if (deltaPct < -0.5) return 'down';
  return 'flat';
}
